/** @jsxImportSource preact */
import { h } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import * as echarts from 'echarts';
import { radarBandColors } from '../data/impactScale';
import { RadarScaleKey } from './RadarScaleKey';
import { wrapAxisName, radarRadius } from './radarLayout';

export interface SeverityRadarCategory {
  label: string;
  direct: number | null;
  indirect: number | null;
}

interface SeverityRadarProps {
  categories: SeverityRadarCategory[];
  /** Canvas height in px. Default 300. */
  height?: number;
}

/**
 * Client-rendered replacement for the pre-generated "Severity Spider"
 * images: a radar of per-category severity ratings (1–5), one polygon for
 * Direct (solid) and one for Indirect (dashed) harm, both in neutral ink
 * over five concentric bands tinted with the impact scale (Negligible →
 * Catastrophic, centre outwards). The RadarScaleKey beneath names each band.
 * Used by the incident detail modal for datasets that carry raw scores but
 * no spider attachment (the Arcola mirror), and works for any category
 * count (10 tracker categories or the extended Arcola set).
 */
export function SeverityRadar({ categories, height = 300 }: SeverityRadarProps) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!ref.current || categories.length < 3) return;

    const el = ref.current;
    const chart = echarts.init(el);
    chartRef.current = chart;
    // Radius follows the container so the names fit on a phone (see radarLayout).
    const radius = () => radarRadius(el.clientWidth, el.clientHeight, 0.58, 80);

    chart.setOption({
      color: ['#111827', '#4b5563'],
      // No tooltip: the all-categories hover list was too noisy. The
      // per-category rows below the radar carry the exact scores.
      tooltip: { show: false },
      legend: {
        top: 0,
        itemWidth: 14,
        itemHeight: 9,
        textStyle: { fontSize: 11 },
      },
      radar: {
        indicator: categories.map((c) => ({ name: c.label, max: 5 })),
        radius: radius(),
        center: ['50%', '54%'],
        axisName: { fontSize: 10, color: '#6b7280', lineHeight: 12, formatter: (name?: string) => wrapAxisName(name ?? '') },
        splitNumber: 5,
        axisLine: { lineStyle: { color: 'rgba(0,0,0,0.12)' } },
        splitLine: { lineStyle: { color: 'rgba(255,255,255,0.9)', width: 1 } },
        splitArea: { areaStyle: { color: radarBandColors() } },
      },
      series: [
        {
          type: 'radar',
          // Not interactive: without this the polygons show a pointer
          // cursor on hover, as if they linked somewhere.
          silent: true,
          symbolSize: 4,
          data: [
            {
              name: 'Direct',
              value: categories.map((c) => c.direct ?? 0),
              lineStyle: { width: 2 },
              areaStyle: { opacity: 0.1 },
            },
            {
              name: 'Indirect',
              value: categories.map((c) => c.indirect ?? 0),
              lineStyle: { width: 2, type: 'dashed' },
              areaStyle: { opacity: 0.05 },
            },
          ],
        },
      ],
    });

    const ro = new ResizeObserver(() => {
      chart.setOption({ radar: { radius: radius() } });
      chart.resize();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, [JSON.stringify(categories)]);

  if (categories.length < 3) return null; // a radar needs ≥3 axes to read

  return (
    <div>
      <div
        ref={ref}
        role="img"
        aria-label="Harm Severity Radar — per-category direct and indirect severity ratings, Negligible to Catastrophic"
        style={{ width: '100%', height: `${height}px` }}
      />
      <RadarScaleKey variant="standard" />
    </div>
  );
}
